import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import {
	gsap,
} from "gsap";

import styles from "./MarqueeTitle.module.css";


/*
 * ============================================================
 * CONFIG
 * ============================================================
 */

const DEFAULT_SPEED =
	38;

const DEFAULT_START_DELAY =
	0.9;

const DEFAULT_END_DELAY =
	0.9;

const MASK_FADE_DURATION =
	0.22;

const OVERFLOW_TOLERANCE =
	2;


/*
 * ============================================================
 * MARQUEE MOTION EASE
 * ============================================================
 */

const MARQUEE_EDGE_RATIO =
	0.12;

const MARQUEE_EASE_NAME =
	"marqueeCruise";

const MARQUEE_CRUISE_VELOCITY =
	1 /
	(1 -
		MARQUEE_EDGE_RATIO);


const marqueeCruiseEase =
	(progress) => {
		const edge =
			MARQUEE_EDGE_RATIO;

		const velocity =
			MARQUEE_CRUISE_VELOCITY;

		if (
			progress <
			edge
		) {
			return (
				velocity *
				progress *
				progress /
				(
					2 *
					edge
				)
			);
		}

		if (
			progress <=
			1 -
				edge
		) {
			return (
				velocity *
				(
					progress -
					edge /
						2
				)
			);
		}

		const remaining =
			1 -
			progress;

		return (
			1 -
			velocity *
				remaining *
				remaining /
				(
					2 *
					edge
				)
		);
	};


gsap.registerEase(
	MARQUEE_EASE_NAME,
	marqueeCruiseEase,
);


/*
 * ============================================================
 * COMPONENT
 * ============================================================
 */

const MarqueeTitle = ({
	children,
	className = "",
	measure = null,
	visible = true,
	active = false,
	speed = DEFAULT_SPEED,
	startDelay = DEFAULT_START_DELAY,
	endDelay = DEFAULT_END_DELAY,
}) => {
	const viewportRef =
		useRef(
			null,
		);

	const trackRef =
		useRef(
			null,
		);

	const timelineRef =
		useRef(
			null,
		);

	const fallbackMeasurementFrameRef =
		useRef(
			null,
		);


	/*
	 * ========================================================
	 * GEOMETRY REFS
	 * ========================================================
	 *
	 * These contain the latest dimensions supplied either by:
	 *
	 * 1. the initial synchronous pre-measure
	 *
	 * or
	 *
	 * 2. ResizeObserver
	 *
	 *
	 * After initialization ResizeObserver is authoritative.
	 *
	 * Its callback does not manually read DOM geometry.
	 */

	const viewportWidthRef =
		useRef(
			0,
		);

	const trackWidthRef =
		useRef(
			0,
		);


	const activeRef =
		useRef(
			active,
		);

	activeRef.current =
		active;


	/*
	 * ========================================================
	 * MEASUREMENT ENABLED
	 * ========================================================
	 *
	 * Measurement is deliberately separate from visibility.
	 *
	 *
	 * Fullscreen:
	 *
	 * measure = true
	 *
	 * while the incoming title can still be visually hidden.
	 *
	 *
	 * Existing callers that do not supply `measure` keep the
	 * original behavior:
	 *
	 * measurementEnabled === visible
	 */

	const measurementEnabled =
		measure ??
		visible;


	const [
		overflowDistance,
		setOverflowDistance,
	] = useState(
		0,
	);


	const hasOverflow =
		overflowDistance >
		OVERFLOW_TOLERANCE;


	/*
	 * ========================================================
	 * UPDATE OVERFLOW
	 * ========================================================
	 *
	 * Pure calculation.
	 *
	 * No DOM reads.
	 */

	const updateOverflow =
		useCallback(
			(
				viewportWidth,
				trackWidth,
			) => {
				if (
					viewportWidth <=
						0 ||
					trackWidth <=
						0
				) {
					return false;
				}


				const rawDistance =
					trackWidth -
					viewportWidth;


				const nextDistance =
					rawDistance >
					OVERFLOW_TOLERANCE
						? rawDistance
						: 0;


				setOverflowDistance(
					(
						currentDistance,
					) => {
						/*
						 * Ignore insignificant fractional differences.
						 *
						 * This prevents tiny ResizeObserver geometry
						 * fluctuations from rebuilding the timeline.
						 */

						if (
							Math.abs(
								currentDistance -
									nextDistance,
							) <
							0.5
						) {
							return currentDistance;
						}


						return nextDistance;
					},
				);


				return true;
			},
			[],
		);


	/*
	 * ========================================================
	 * INITIAL PRE-MEASURE
	 * ========================================================
	 *
	 * This is the ONLY normal path that deliberately performs
	 * synchronous geometry reads.
	 *
	 *
	 * Why keep it?
	 *
	 * The fullscreen title needs its overflow state before the
	 * incoming case becomes visible.
	 *
	 * Waiting for ResizeObserver alone can introduce the mask
	 * one frame too late.
	 *
	 *
	 * Therefore:
	 *
	 * title changes
	 * ↓
	 * useLayoutEffect
	 * ↓
	 * one synchronous geometry check
	 * ↓
	 * correct overflow state before paint
	 *
	 *
	 * After that, ResizeObserver takes over.
	 */

	const preMeasure =
		useCallback(() => {
			const viewport =
				viewportRef.current;

			const track =
				trackRef.current;


			if (
				!viewport ||
				!track
			) {
				return false;
			}


			/*
			 * getBoundingClientRect gives us fractional geometry,
			 * which matches ResizeObserver more closely than
			 * integer clientWidth / scrollWidth values.
			 */

			const viewportWidth =
				viewport.getBoundingClientRect()
					.width;

			const trackWidth =
				track.getBoundingClientRect()
					.width;


			if (
				viewportWidth <=
					0 ||
				trackWidth <=
					0
			) {
				return false;
			}


			viewportWidthRef.current =
				viewportWidth;

			trackWidthRef.current =
				trackWidth;


			return updateOverflow(
				viewportWidth,
				trackWidth,
			);
		}, [
			updateOverflow,
		]);


	/*
	 * ========================================================
	 * PRE-MEASURE ON TITLE / MEASUREMENT CHANGE
	 * ========================================================
	 *
	 * The normal path performs one synchronous pre-measure.
	 *
	 *
	 * RAF is ONLY a fallback for a temporarily collapsed element
	 * that reports width 0.
	 */

	useLayoutEffect(() => {
		if (
			fallbackMeasurementFrameRef.current
		) {
			cancelAnimationFrame(
				fallbackMeasurementFrameRef.current,
			);

			fallbackMeasurementFrameRef.current =
				null;
		}


		if (
			!measurementEnabled
		) {
			viewportWidthRef.current =
				0;

			trackWidthRef.current =
				0;


			setOverflowDistance(
				(
					currentDistance,
				) => {
					if (
						currentDistance ===
						0
					) {
						return currentDistance;
					}


					return 0;
				},
			);


			return;
		}


		const measurementValid =
			preMeasure();


		/*
		 * Only fall back to one RAF if the element is currently
		 * not layoutable.
		 */

		if (
			!measurementValid
		) {
			fallbackMeasurementFrameRef.current =
				requestAnimationFrame(
					() => {
						fallbackMeasurementFrameRef.current =
							null;


						preMeasure();
					},
				);
		}


		return () => {
			if (
				fallbackMeasurementFrameRef.current
			) {
				cancelAnimationFrame(
					fallbackMeasurementFrameRef.current,
				);

				fallbackMeasurementFrameRef.current =
					null;
			}
		};
	}, [
		measurementEnabled,
		children,
		preMeasure,
	]);


	/*
	 * ========================================================
	 * RESIZE OBSERVER
	 * ========================================================
	 *
	 * Observe BOTH:
	 *
	 * - viewport
	 * - track
	 *
	 *
	 * This automatically handles:
	 *
	 * - responsive viewport changes
	 * - mobile ↔ desktop breakpoint changes
	 * - orientation changes
	 * - font-size changes
	 * - font-family/font loading changes
	 * - letter-spacing changes
	 * - inherited typography changes
	 *
	 *
	 * IMPORTANT:
	 *
	 * We do NOT read:
	 *
	 * clientWidth
	 * scrollWidth
	 * getBoundingClientRect()
	 *
	 * inside this callback.
	 *
	 *
	 * ResizeObserver already gives us the geometry.
	 */

	useLayoutEffect(() => {
		if (
			!measurementEnabled
		) {
			return;
		}


		const viewport =
			viewportRef.current;

		const track =
			trackRef.current;


		if (
			!viewport ||
			!track
		) {
			return;
		}


		if (
			typeof ResizeObserver !==
			"undefined"
		) {
			const resizeObserver =
				new ResizeObserver(
					(entries) => {
						let geometryChanged =
							false;


						entries.forEach(
							(entry) => {
								const width =
									entry
										.contentRect
										.width;


								if (
									width <=
									0
								) {
									return;
								}


								if (
									entry.target ===
									viewport
								) {
									viewportWidthRef.current =
										width;

									geometryChanged =
										true;
								}


								if (
									entry.target ===
									track
								) {
									trackWidthRef.current =
										width;

									geometryChanged =
										true;
								}
							},
						);


						if (
							!geometryChanged
						) {
							return;
						}


						updateOverflow(
							viewportWidthRef.current,
							trackWidthRef.current,
						);
					},
				);


			resizeObserver.observe(
				viewport,
			);

			resizeObserver.observe(
				track,
			);


			return () => {
				resizeObserver.disconnect();
			};
		}


		/*
		 * ====================================================
		 * FALLBACK
		 * ====================================================
		 *
		 * Older browsers without ResizeObserver.
		 *
		 * Only this fallback performs geometry reads on resize.
		 */

		const handleResize =
			() => {
				preMeasure();
			};


		window.addEventListener(
			"resize",
			handleResize,
		);


		return () => {
			window.removeEventListener(
				"resize",
				handleResize,
			);
		};
	}, [
		measurementEnabled,
		preMeasure,
		updateOverflow,
	]);


	/*
	 * ========================================================
	 * FONT LOADING
	 * ========================================================
	 *
	 * No document.fonts.ready measurement is necessary anymore.
	 *
	 *
	 * If the final font changes the title geometry:
	 *
	 * font changes
	 * ↓
	 * track size changes
	 * ↓
	 * ResizeObserver fires
	 * ↓
	 * overflow recalculates
	 *
	 *
	 * This removes another explicit measurement path.
	 */


	/*
	 * ========================================================
	 * RESET
	 * ========================================================
	 */

	const resetToStart =
		useCallback(() => {
			const viewport =
				viewportRef.current;

			const track =
				trackRef.current;

			const timeline =
				timelineRef.current;


			if (
				timeline
			) {
				timeline.pause(
					0,
				);
			}


			if (
				track
			) {
				gsap.set(
					track,
					{
						x:
							0,
					},
				);
			}


			if (
				viewport
			) {
				gsap.set(
					viewport,
					{
						"--marquee-left-edge":
							1,

						"--marquee-right-edge":
							hasOverflow
								? 0
								: 1,
					},
				);
			}
		}, [
			hasOverflow,
		]);


	/*
	 * ========================================================
	 * TIMELINE
	 * ========================================================
	 *
	 * Measurement may happen ahead of visibility.
	 *
	 *
	 * Expensive animation machinery still only exists when:
	 *
	 * visible
	 * &&
	 * hasOverflow
	 */

	useLayoutEffect(() => {
		const viewport =
			viewportRef.current;

		const track =
			trackRef.current;


		/*
		 * Geometry changed.
		 *
		 * Kill the old geometry-specific timeline before deciding
		 * whether another one is needed.
		 */

		if (
			timelineRef.current
		) {
			timelineRef.current.kill();

			timelineRef.current =
				null;
		}


		if (
			!viewport ||
			!track
		) {
			return;
		}


		/*
		 * Always restore the text position before rebuilding.
		 */

		gsap.set(
			track,
			{
				x:
					0,
			},
		);


		/*
		 * ====================================================
		 * HIDDEN
		 * ====================================================
		 *
		 * Geometry may already be known.
		 *
		 * Prepare the correct static mask state, but do not build
		 * a timeline.
		 */

		if (
			!visible
		) {
			gsap.set(
				viewport,
				{
					"--marquee-left-edge":
						1,

					"--marquee-right-edge":
						hasOverflow
							? 0
							: 1,
				},
			);


			return;
		}


		/*
		 * ====================================================
		 * SHORT TITLE
		 * ====================================================
		 */

		if (
			!hasOverflow
		) {
			gsap.set(
				viewport,
				{
					"--marquee-left-edge":
						1,

					"--marquee-right-edge":
						1,
				},
			);


			return;
		}


		/*
		 * ====================================================
		 * OVERFLOWING TITLE
		 * ====================================================
		 */

		gsap.set(
			viewport,
			{
				"--marquee-left-edge":
					1,

				"--marquee-right-edge":
					0,
			},
		);


		/*
		 * Reduced-motion users keep the static overflow hint,
		 * without movement.
		 */

		const prefersReducedMotion =
			typeof window !==
				"undefined" &&
			window.matchMedia?.(
				"(prefers-reduced-motion: reduce)",
			).matches;


		if (
			prefersReducedMotion
		) {
			return;
		}


		const safeSpeed =
			Math.max(
				1,
				speed,
			);


		/*
		 * Custom easing cruises slightly faster through the
		 * middle.
		 *
		 * Compensate duration so perceived cruise speed remains
		 * equal to `speed`.
		 */

		const travelDuration =
			(
				overflowDistance *
					MARQUEE_CRUISE_VELOCITY
			) /
			safeSpeed;


		const rightMaskStart =
			Math.max(
				0,
				travelDuration -
					MASK_FADE_DURATION,
			);


		const timeline =
			gsap.timeline({
				paused:
					true,

				repeat:
					-1,

				yoyo:
					true,

				repeatDelay:
					endDelay,

				delay:
					startDelay,
			});


		/*
		 * ====================================================
		 * START MASK
		 * ====================================================
		 */

		timeline.to(
			viewport,
			{
				"--marquee-left-edge":
					0,

				duration:
					MASK_FADE_DURATION,

				ease:
					"power1.out",
			},
			"travel",
		);


		/*
		 * ====================================================
		 * MOVEMENT
		 * ====================================================
		 */

		timeline.to(
			track,
			{
				x:
					-overflowDistance,

				duration:
					travelDuration,

				ease:
					MARQUEE_EASE_NAME,
			},
			"travel",
		);


		/*
		 * ====================================================
		 * END MASK
		 * ====================================================
		 */

		timeline.to(
			viewport,
			{
				"--marquee-right-edge":
					1,

				duration:
					MASK_FADE_DURATION,

				ease:
					"power1.out",
			},
			`travel+=${rightMaskStart}`,
		);


		timelineRef.current =
			timeline;


		/*
		 * Timeline creation intentionally does not depend on the
		 * active prop.
		 *
		 * If geometry changed while already active, restart the
		 * freshly-created timeline.
		 */

		if (
			activeRef.current
		) {
			timeline.restart(
				true,
			);
		}


		return () => {
			timeline.kill();


			if (
				timelineRef.current ===
					timeline
			) {
				timelineRef.current =
					null;
			}
		};
	}, [
		visible,
		hasOverflow,
		overflowDistance,
		speed,
		startDelay,
		endDelay,
	]);


	/*
	 * ========================================================
	 * ACTIVE STATE
	 * ========================================================
	 *
	 * active = true
	 *
	 * → fresh marquee cycle
	 *
	 *
	 * active = false
	 *
	 * → immediately return to the beginning
	 */

	useEffect(() => {
		const timeline =
			timelineRef.current;


		if (
			active &&
			timeline
		) {
			timeline.restart(
				true,
			);


			return;
		}


		if (
			!active
		) {
			resetToStart();
		}
	}, [
		active,
		resetToStart,
	]);


	/*
	 * ========================================================
	 * UNMOUNT CLEANUP
	 * ========================================================
	 */

	useEffect(() => {
		return () => {
			if (
				fallbackMeasurementFrameRef.current
			) {
				cancelAnimationFrame(
					fallbackMeasurementFrameRef.current,
				);

				fallbackMeasurementFrameRef.current =
					null;
			}


			if (
				timelineRef.current
			) {
				timelineRef.current.kill();

				timelineRef.current =
					null;
			}
		};
	}, []);


	/*
	 * ========================================================
	 * RENDER
	 * ========================================================
	 */

	return (
		<div
			ref={
				viewportRef
			}
			className={`${styles.viewport}${
				className
					? ` ${className}`
					: ""
			}${
				hasOverflow
					? ` ${styles.overflow}`
					: ""
			}${
				visible &&
				hasOverflow &&
				active
					? ` ${styles.active}`
					: ""
			}`}
			title={
				typeof children ===
					"string"
					? children
					: undefined
			}
		>
			<div
				ref={
					trackRef
				}
				className={
					styles.track
				}
			>
				{children}
			</div>
		</div>
	);
};


export default MarqueeTitle;